import NavigationCard, { BaseImage } from '../../../navigation/NavigationCard';
import { InternalNavigation } from '../../../navigation/helper';
import React from 'react';
import {
  CustomListsNavigationCard,
  QueryTemplatesNavigationCard,
  SegmentsNavigationCard,
} from './CommonNavigationCards';
import {
  CAN_READ_CUSTOM_LIST,
  CAN_READ_ENTITY_GROUP,
  CAN_READ_QUERY_TEMPLATE,
  CAN_READ_SANCTION_CONFIGURATION,
  CAN_READ_SCREENING_CLEARANCE_RULE,
  CAN_VIEW_ATTRIBUTE_MAPPING_MENU,
} from '../../../auth/permissions';
import Has from '../../../auth/Has';
import { InternalApiEntityType } from '../../../api-internal';
import { createSearchParams } from 'react-router';
import {
  faArrowRightArrowLeft,
  faClockRotateLeft,
  faPeopleSimple,
  faRotateExclamation,
  faSquareCheck,
  faUsersViewfinder,
} from '@fortawesome/pro-duotone-svg-icons';
import { SearchParamKeys } from '../../../common/searchParamKeys';

const Screening = () => {
  const getScreeningFlows = (entityType: InternalApiEntityType) => ({
    pathname: InternalNavigation.screeningFlows,
    search: createSearchParams({ [SearchParamKeys.entityType]: entityType }).toString(),
  });

  return (
    <>
      <Has anyPermissions={[CAN_READ_SANCTION_CONFIGURATION, CAN_VIEW_ATTRIBUTE_MAPPING_MENU]}>
        <h1 className={'m-b-8'}>Screening Flows</h1>
        <div style={{ display: 'flex', gap: '32px' }}>
          <Has allPermissions={CAN_READ_SANCTION_CONFIGURATION}>
            <NavigationCard
              title={'Transaction Screening Flows'}
              description={
                'Implement Screening logic for Transaction data with Screened Fields, Goodlisting, Matching Types, and Lists per Segment.'
              }
              to={getScreeningFlows(InternalApiEntityType.TRANSACTION)}
              image={{
                base: BaseImage.base4,
                icon: faArrowRightArrowLeft,
              }}
            />
          </Has>
          <Has allPermissions={CAN_READ_SANCTION_CONFIGURATION}>
            <NavigationCard
              title={'Person Screening Flows'}
              description={
                'Implement Screening logic for Person data with Screened Fields, Matching Types, Lists and List Groups for each Segment.'
              }
              to={getScreeningFlows(InternalApiEntityType.PERSON)}
              image={{
                base: BaseImage.base4,
                icon: faPeopleSimple,
              }}
            />
          </Has>
        </div>
      </Has>
      <Has anyPermissions={[CAN_READ_ENTITY_GROUP, CAN_READ_SANCTION_CONFIGURATION, CAN_READ_CUSTOM_LIST]}>
        <h1 className={'m-b-8 m-t-32'}>Flow Inputs</h1>
        <div style={{ display: 'flex', gap: '32px' }}>
          <SegmentsNavigationCard />
          <Has allPermissions={CAN_READ_SANCTION_CONFIGURATION}>
            <NavigationCard
              title={'List Groups'}
              description={
                'Create and manage Dow Jones Sanctions List Groups for use in Person and Transaction Screening Flows.'
              }
              to={InternalNavigation.listGroups}
              image={{
                base: BaseImage.base2,
                icon: faUsersViewfinder,
              }}
            />
          </Has>
          <CustomListsNavigationCard />
        </div>
      </Has>
      <Has
        anyPermissions={[CAN_READ_SANCTION_CONFIGURATION, CAN_READ_SCREENING_CLEARANCE_RULE, CAN_READ_QUERY_TEMPLATE]}
      >
        <h1 className={'m-b-8 m-t-32'}>Alert Optimisation</h1>
        <div style={{ display: 'flex', gap: '32px' }}>
          <Has allPermissions={CAN_READ_SANCTION_CONFIGURATION}>
            <NavigationCard
              title={'Significant Update Indicators'}
              description={
                'Choose changes in your data and Dow Jones lists that warrant a full re-screening in subsequent events.'
              }
              to={InternalNavigation.significantUpdateIndicators}
              image={{
                base: BaseImage.base5,
                icon: faRotateExclamation,
              }}
            />
          </Has>
          <Has allPermissions={CAN_READ_SANCTION_CONFIGURATION}>
            <NavigationCard
              title={'Automatic Re-Screening'}
              description={'Select Person Segments to exclude from being automatically re-screened.'}
              to={InternalNavigation.automaticReScreening}
              image={{
                base: BaseImage.base5,
                icon: faClockRotateLeft,
              }}
            />
          </Has>
          <Has allPermissions={CAN_READ_SCREENING_CLEARANCE_RULE}>
            <NavigationCard
              title={'Clearance Rules'}
              description={
                'Add and edit Clearance Rules for Person and Transaction Screening, and use Impact Analysis to test their effectiveness in reducing Alerts.'
              }
              to={InternalNavigation.clearanceRules}
              image={{
                base: BaseImage.base5,
                icon: faSquareCheck,
              }}
            />
          </Has>
          <QueryTemplatesNavigationCard base={BaseImage.base5} />
        </div>
      </Has>
    </>
  );
};

export default Screening;
